// Simple OAuth utilities - much cleaner approach

// Import logo assets
import { AnalyticsEvents } from '@mondaygpt/analytics-events'
import {
  getOAuthProviderDisplayName,
  getOAuthProviderLogoUrl,
  isOAuthProviderComingSoon,
} from '@mondaygpt/constants'
import { logger } from 'services/error-reporting/error-reporting'
import takaLogo from 'assets/taka-logo-icon.svg'
import { OpenAPI } from 'openapi'
import { toast } from 'sonner'

/**
 * Map an internal OAuth provider id to the user-facing one. `linkedin_org` (the
 * Community Management app behind Company Page posting) surfaces as plain
 * `linkedin` — a single connect card. Normalizing through this avoids scattering
 * the alias across every name/icon/logo lookup.
 */
function toCardProvider(provider: string): string {
  return provider === 'linkedin_org' ? 'linkedin' : provider
}

/**
 * Whether a server `OAuthUpdated` event's provider refers to the same connect
 * the popup was opened for. Normalizes BOTH sides through `toCardProvider` so the
 * Company-Page alias matches: a page connect opens the popup with `linkedin_org`,
 * but the server stores it under the `linkedin` row and emits `OAuthUpdated` with
 * provider `linkedin`. Without this, the fast Pusher confirmation never matches
 * for page connects and we fall back to the slow popup-closed + 20s timeout path.
 */
function providerMatchesEvent(eventProvider: string | undefined, wantProvider: string): boolean {
  if (!eventProvider) return false
  return toCardProvider(eventProvider.toLowerCase()) === toCardProvider(wantProvider.toLowerCase())
}

/**
 * Where the user clicked Connect from. MUST match the `source` enum on
 * social_oauth_initiated in packages/node/analytics-events/categories/social.yaml
 * and the KNOWN_INITIATOR_SOURCES list in apps/server/src/oauth/oauth.controller.ts.
 */
export type OAuthInitiatorSource =
  | 'onboarding'
  | 'composer'
  | 'settings'
  | 'post_modal'
  | 'creative_card'
  | 'autopilot'
  | 'integrations_page'
  | 'comment_automation'

/**
 * Minimal track signature so we don't drag the React useAnalytics hook into a
 * util file. Matches the shape `useAnalytics().track` returns — callers pass
 * it directly without coercion.
 */
export type OAuthTrackPropertyValue = string | number | boolean
export type OAuthTrackProperties = {
  [key: string]: OAuthTrackPropertyValue | OAuthTrackPropertyValue[] | null | undefined
}
export type OAuthTrackFn = (eventName: string, properties?: OAuthTrackProperties) => void

export interface OpenOAuthPopupOptions {
  businessId?: string
  /**
   * Where the user clicked Connect from. Required so we can build a
   * "click → callback" funnel and attribute popup-blocker drop-offs per
   * surface. Defaults to 'settings' for legacy callers but every new caller
   * should pass an explicit value.
   */
  source?: OAuthInitiatorSource
  /**
   * Optional analytics track function (typically `useAnalytics().track`). When
   * provided, fires SOCIAL_OAUTH_INITIATED on click and
   * SOCIAL_OAUTH_INITIATED_BLOCKED if the browser blocks the popup. Without
   * this, the click is invisible to Mixpanel — exactly the gap that hid the
   * popup-blocker first-attempt failures users were reporting.
   */
  track?: OAuthTrackFn
}

function oauthPopupFeatures(provider: string): string {
  if (provider !== 'facebook') return 'width=500,height=600,scrollbars=yes,resizable=yes'
  const width = 710
  const height = 800
  // Centered on the app's browser window, not the screen, so it lands on the right monitor.
  const left = Math.round(window.screenX + (window.outerWidth - width) / 2)
  const top = Math.round(window.screenY + (window.outerHeight - height) / 2)
  return `width=${width},height=${height},left=${left},top=${top},scrollbars=yes,resizable=yes`
}

export const oauthUtils = {
  /**
   * Open OAuth popup for any provider
   * Listens for: localStorage, BroadcastChannel, AND Pusher event (server confirmation)
   * @returns true = success, false = explicit failure, null = popup closed + no Pusher confirmation within 20s
   */
  async openOAuthPopup(provider: string, options?: OpenOAuthPopupOptions): Promise<boolean | null> {
    const source = options?.source ?? 'settings'
    const track = options?.track

    // Click-time analytics: fire BEFORE we touch window.open. Browsers count
    // every visible popup attempt as a popup, blocked or not, so this is the
    // only point where we know the user actually clicked. Without this event,
    // popup-blocked failures look identical to "user never clicked" — which is
    // why the SERVER-side funnel never showed any signal for the matanla and
    // charles "succeeded only on second try" reports.
    // Normalize the Company-Page alias (`linkedin_org`) to the `linkedin` card so
    // page connects land in the same connect funnel as personal — the event's
    // `platform` enum has no `linkedin_org` value, and the server emits `linkedin`.
    const platform = toCardProvider(provider)
    track?.(AnalyticsEvents.Social.SOCIAL_OAUTH_INITIATED, {
      platform,
      source,
      business_id: options?.businessId,
    })
    // Unified connect funnel (dual-emit during migration). This is the single
    // choke point for every direct OAuth popup, so it's the one client-side
    // emitter of social_connect_initiated for social_gateway 'direct'. Partner
    // gateways emit their own initiated from the gateway/fallback connect hooks.
    track?.(AnalyticsEvents.Social.SOCIAL_CONNECT_INITIATED, {
      platform,
      social_gateway: 'direct',
      source,
      business_id: options?.businessId,
    })

    try {
      // Read the access token from in-memory state (the OpenAPI client mirror).
      // localStorage is no longer the source of truth — refresh tokens are in an
      // HttpOnly cookie and access tokens live in Redux memory.
      const token = typeof OpenAPI.TOKEN === 'string' ? OpenAPI.TOKEN : undefined
      if (!token) {
        return false
      }

      // VITE_API_URL is empty in dev (we go through the vite proxy for same-origin
      // cookie support), so coalesce with `??` (only on null/undefined) and fall
      // back to a relative path when the var was set to an empty string.
      const rawBase = import.meta.env.VITE_API_URL
      const apiBaseUrl = typeof rawBase === 'string' ? rawBase : 'http://localhost:8000'
      let oauthUrl = `${apiBaseUrl}/v1/auth/${provider}?token=${encodeURIComponent(token)}`
      if (options?.businessId) {
        oauthUrl += `&businessId=${encodeURIComponent(options.businessId)}`
      }
      // Source is echoed to the server so the SERVER-side SOCIAL_OAUTH_INITIATED
      // event carries the same attribution. Server validates against an allowlist.
      oauthUrl += `&source=${encodeURIComponent(source)}`

      const popupOpenedAt = Date.now()
      const popup = window.open('about:blank', 'oauth-popup', oauthPopupFeatures(provider))

      if (!popup) {
        logger.warn('Popup blocked. Please allow popups for this site.')
        track?.(AnalyticsEvents.Social.SOCIAL_OAUTH_INITIATED_BLOCKED, {
          platform,
          source,
        })
        toast.error('Your browser blocked the connection popup', {
          description: `Allow popups for this site, then click Connect ${this.getProviderName(provider)} again.`,
        })
        return false
      }

      const providerName = this.getProviderName(provider)
      const providerLogo = this.getProviderLogo(provider)

      // Create modern loading page content - sized for mobile visibility
      const loadingHTML = `
        <!DOCTYPE html>
        <html>
        <head>
          <title>Connecting to ${providerName}</title>
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <style>
            * {
              margin: 0;
              padding: 0;
              box-sizing: border-box;
            }
            body {
              font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', sans-serif;
              background: #08090b;
              display: flex;
              align-items: center;
              justify-content: center;
              min-height: 100vh;
              color: #f5f7fa;
            }
            .container {
              text-align: center;
              padding: 2rem;
              max-width: 400px;
              width: 100%;
            }
            .logos {
              display: flex;
              align-items: center;
              justify-content: center;
              gap: 1.5rem;
              margin-bottom: 2.5rem;
            }
            .logo {
              width: 80px;
              height: 80px;
              border-radius: 20px;
              display: flex;
              align-items: center;
              justify-content: center;
              box-shadow: 0 4px 12px rgba(0,0,0,0.4);
              overflow: hidden;
              background: #fff;
            }
            /* Taka's icon-only SVG already contains its own dark chip
               (chip-on-T invariant, see playbook/design-system/logo.md).
               The wrapper goes transparent and the SVG fills the tile so we
               don't double-stack chips (visible ring around the inner chip).
               The .logo outer's border-radius + overflow:hidden still clip
               the SVG's own corners flush with the partner tile. */
            .logo--taka {
              background: transparent;
            }
            .logo img {
              width: 70%;
              height: 70%;
              object-fit: contain;
            }
            .logo--taka img {
              width: 100%;
              height: 100%;
            }
            .connector {
              display: flex;
              align-items: center;
              gap: 4px;
            }
            .arrow {
              color: #777f8c;
              font-size: 20px;
              animation: flow 1.2s ease-in-out infinite;
            }
            .arrow:nth-child(1) { animation-delay: 0s; }
            .arrow:nth-child(2) { animation-delay: 0.15s; }
            .arrow:nth-child(3) { animation-delay: 0.3s; }
            @keyframes flow {
              0%, 100% { 
                opacity: 0.3;
                transform: translateX(0);
              }
              50% { 
                opacity: 1;
                transform: translateX(3px);
              }
            }
            .title {
              font-size: 1.5rem;
              font-weight: 600;
              color: #f5f7fa;
              margin-bottom: 0.75rem;
            }
            .subtitle {
              font-size: 1rem;
              color: #8b92a0;
              line-height: 1.5;
            }
            .spinner-container {
              margin-top: 2.5rem;
            }
            .spinner {
              width: 36px;
              height: 36px;
              border: 3px solid #20242c;
              border-top-color: #c6ff3d;
              border-radius: 50%;
              animation: spin 0.8s linear infinite;
              margin: 0 auto;
            }
            @keyframes spin {
              to { transform: rotate(360deg); }
            }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="logos">
              <div class="logo logo--taka">
                <img src="${takaLogo}" alt="Taka" />
              </div>
              <div class="connector">
                <span class="arrow">›</span>
                <span class="arrow">›</span>
                <span class="arrow">›</span>
              </div>
              <div class="logo">
                <img src="${providerLogo}" alt="${providerName}" />
              </div>
            </div>
            <div class="title">Connecting to ${providerName}</div>
            <div class="subtitle">You'll be redirected to authorize access</div>
            <div class="spinner-container">
              <div class="spinner"></div>
            </div>
          </div>
        </body>
        </html>
      `

      // Write loading content to popup
      popup.document.write(loadingHTML)
      popup.document.close()

      // Redirect to OAuth URL after a short delay
      window.setTimeout(() => {
        if (!popup.closed) {
          popup.location.href = oauthUrl
        }
      }, 800)

      // Clear any previous OAuth result before starting
      try {
        localStorage.removeItem('oauth-result')
      } catch {
        // Ignore localStorage errors
      }

      return new Promise<boolean | null>(resolve => {
        let channel: BroadcastChannel | null = null
        let resolved = false
        let popupClosed = false
        let pusherTimeoutId: ReturnType<typeof setTimeout> | null = null

        const resolveOnce = (success: boolean | null) => {
          if (resolved) return
          resolved = true
          cleanup()
          resolve(success)
        }

        const handleBroadcastMessage = (event: MessageEvent) => {
          if (event.data?.type === 'oauth-success') {
            resolveOnce(true)
          } else if (event.data?.type === 'oauth-error') {
            resolveOnce(false)
          }
        }

        // Listen for Pusher event (server-confirmed success) - most reliable!
        const handlePusherSuccess = (event: Event) => {
          const customEvent = event as CustomEvent<{ provider: string }>
          if (providerMatchesEvent(customEvent.detail?.provider, provider)) {
            logger.debug('OAuth: Success confirmed via Pusher event')
            resolveOnce(true)
          }
        }

        // Check localStorage for OAuth result (synchronous, reliable)
        const checkLocalStorageResult = (): boolean | null => {
          try {
            const resultStr = localStorage.getItem('oauth-result')
            if (!resultStr) return null

            const result = JSON.parse(resultStr)
            // Only accept results from the last 30 seconds
            if (Date.now() - result.timestamp > 30000) return null

            localStorage.removeItem('oauth-result') // Clean up
            return result.type === 'success'
          } catch {
            return null
          }
        }

        const cleanup = () => {
          window.removeEventListener('oauth-server-success', handlePusherSuccess)
          if (channel) {
            channel.removeEventListener('message', handleBroadcastMessage)
            channel.close()
            channel = null
          }
          if (popup && !popup.closed) {
            popup.close()
          }
          if (checkClosedInterval) {
            window.clearInterval(checkClosedInterval)
          }
          if (pusherTimeoutId) {
            clearTimeout(pusherTimeoutId)
          }
        }

        // Start listening for Pusher event FIRST (before anything else)
        window.addEventListener('oauth-server-success', handlePusherSuccess)

        // Setup BroadcastChannel for immediate notification (best-effort)
        try {
          if (typeof BroadcastChannel !== 'undefined') {
            channel = new BroadcastChannel('oauth-channel')
            channel.addEventListener('message', handleBroadcastMessage)
          } else {
            logger.debug('BroadcastChannel is not supported')
          }
        } catch (err) {
          logger.debug('Failed to create BroadcastChannel:', { err })
        }

        // Poll for result - check localStorage and popup status
        const checkClosedInterval = window.setInterval(() => {
          // First, always check localStorage for explicit result
          const result = checkLocalStorageResult()
          if (result === true) {
            resolveOnce(true)
            return
          } else if (result === false) {
            resolveOnce(false)
            return
          }

          // Check if popup is closed (wrap in try-catch for cross-origin safety)
          let isClosed = false
          try {
            isClosed = popup.closed
          } catch {
            // Cross-origin access error - can't determine state, keep polling
            return
          }

          if (isClosed && !popupClosed) {
            popupClosed = true
            // Unified popup-closure telemetry — fires once per surface so
            // every entry point (composer/settings/post_modal/creative_card/
            // autopilot/integrations_page) joins the same funnel. Sub-second
            // closures = popup blocker / instant bail; tens of seconds =
            // user reached the provider and abandoned. This is the missing
            // link between social_oauth_initiated (click) and
            // social_oauth_callback_success/_failed (server) when the popup
            // never reaches our callback handler.
            track?.(AnalyticsEvents.Social.SOCIAL_OAUTH_POPUP_CLOSED, {
              platform,
              source,
              seconds_open: (Date.now() - popupOpenedAt) / 1000,
            })
            // Popup closed without explicit localStorage result
            // Wait up to 10 seconds for Pusher event (server confirmation)
            logger.debug('OAuth: Popup closed, waiting for Pusher confirmation...')
            pusherTimeoutId = setTimeout(() => {
              if (!resolved) {
                // No Pusher event received - resolve as unknown
                logger.debug('OAuth: No Pusher confirmation received, resolving as unknown')
                resolveOnce(null)
              }
            }, 20000)
          }
        }, 500)
      })
    } catch (error) {
      logger.warn('Failed to initiate OAuth flow:', { error })
      return false
    }
  },

  /**
   * Wait briefly for the server to finish persisting an OAuth connection.
   *
   * Why this exists: the popup can self-close via localStorage (which the
   * 500ms parent poll picks up first) BEFORE the server's OAuthUpdated Pusher
   * event fires. Callers that immediately call `getIntegrationStatusV1` after
   * `openOAuthPopup` resolves were racing the ~800ms gap between FB callback
   * completion and final DB row persistence, getting `isConnected: false`,
   * and surfacing "Connection cancelled" toasts on a successful OAuth.
   *
   * Resolves on whichever happens first:
   * - `oauth-server-success` DOM event for this provider (dispatched by
   *   pusher.context.tsx when the server fires OAuthUpdated)
   * - `maxWaitMs` timeout (fallback — server may have persisted before we
   *   started listening, or the user's Pusher socket is offline)
   *
   * Call this AFTER `openOAuthPopup` resolves with a truthy result and
   * BEFORE the status check. Safe to no-op on `false` results.
   */
  async waitForOAuthPersistence(provider: string, maxWaitMs = 1500): Promise<void> {
    return new Promise<void>(resolve => {
      const handler = (event: Event) => {
        const customEvent = event as CustomEvent<{ provider: string }>
        if (providerMatchesEvent(customEvent.detail?.provider, provider)) {
          cleanup()
          resolve()
        }
      }
      const timeoutId = window.setTimeout(() => {
        cleanup()
        resolve()
      }, maxWaitMs)
      const cleanup = () => {
        window.removeEventListener('oauth-server-success', handler)
        window.clearTimeout(timeoutId)
      }
      window.addEventListener('oauth-server-success', handler)
    })
  },

  /**
   * Check if URL is OAuth action
   */
  isOAuthAction(url: string): boolean {
    // Support both old format (/oauth/.../authorize) and new format (/v1/auth/...)
    return (url.includes('/oauth/') && url.includes('/authorize')) || url.includes('/v1/auth/')
  },

  /**
   * Open MCP OAuth popup
   */
  async openMCPOAuthPopup(authUrl: string, serverName: string): Promise<boolean> {
    // Ensure any existing OAuth process is cleaned up before starting a new one
    if (this.mcpOAuthState.popup && !this.mcpOAuthState.popup.closed) {
      this.mcpOAuthState.popup.close()
    }
    this.cleanupMCPOAuth()

    try {
      const popup = window.open(
        authUrl,
        'mcp-oauth-popup',
        'width=600,height=700,scrollbars=yes,resizable=yes'
      )

      if (!popup) {
        throw new Error('Popup blocked. Please allow popups for this site.')
      }

      this.mcpOAuthState.popup = popup

      return new Promise<boolean>((resolve, reject) => {
        const handleSuccess = () => {
          this.cleanupMCPOAuth()
          resolve(true)
        }

        const handleError = (description: string, shouldClosePopup = true) => {
          this.cleanupMCPOAuth(shouldClosePopup)
          reject(new Error(description || 'MCP authentication failed.'))
        }

        // Listener for BroadcastChannel
        const handleBroadcastMessage = (event: MessageEvent) => {
          if (event.data?.type === 'mcp-oauth-success' && event.data?.serverName === serverName) {
            handleSuccess()
          } else if (
            event.data?.type === 'mcp-oauth-error' &&
            (!event.data.serverName || event.data.serverName === serverName)
          ) {
            handleError(event.data.description)
          }
        }

        // Setup BroadcastChannel if supported
        try {
          this.mcpOAuthState.channel = new BroadcastChannel('mcp-oauth-channel')
          this.mcpOAuthState.channel.addEventListener('message', handleBroadcastMessage)
          this.mcpOAuthState.broadcastListener = handleBroadcastMessage
        } catch {
          logger.debug('BroadcastChannel is not supported, falling back to postMessage.')
        }

        // Set a timeout to prevent hanging if the user closes the window manually
        this.mcpOAuthState.timeoutId = window.setTimeout(
          () => {
            handleError('Authentication timed out.', false)
          },
          2 * 60 * 1000
        ) // 2 minutes
      })
    } catch (error) {
      logger.warn('Failed to initiate MCP OAuth flow:', { error })
      this.cleanupMCPOAuth()
      throw error
    }
  },

  /**
   * MCP OAuth state management
   */
  mcpOAuthState: {
    popup: null as Window | null,
    channel: null as BroadcastChannel | null,
    timeoutId: null as number | null,
    broadcastListener: null as ((event: MessageEvent) => void) | null,
    postMessageListener: null as ((event: MessageEvent) => void) | null,
  },

  /**
   * Cleanup MCP OAuth resources
   */
  cleanupMCPOAuth(shouldClosePopup = true) {
    if (this.mcpOAuthState.postMessageListener) {
      window.removeEventListener('message', this.mcpOAuthState.postMessageListener)
      this.mcpOAuthState.postMessageListener = null
    }
    if (this.mcpOAuthState.channel) {
      if (this.mcpOAuthState.broadcastListener) {
        this.mcpOAuthState.channel.removeEventListener(
          'message',
          this.mcpOAuthState.broadcastListener
        )
        this.mcpOAuthState.broadcastListener = null
      }
      this.mcpOAuthState.channel.close()
      this.mcpOAuthState.channel = null
    }
    if (this.mcpOAuthState.timeoutId) {
      window.clearTimeout(this.mcpOAuthState.timeoutId)
      this.mcpOAuthState.timeoutId = null
    }
    if (shouldClosePopup && this.mcpOAuthState.popup && !this.mcpOAuthState.popup.closed) {
      this.mcpOAuthState.popup.close()
    }
    this.mcpOAuthState.popup = null
  },

  /**
   * Extract provider from OAuth URL
   */
  extractProvider(url: string): string | null {
    // Handle new format: /v1/auth/{provider}
    const newFormatMatch = url.match(/\/v1\/auth\/([^\/\?]+)/)
    if (newFormatMatch) {
      return newFormatMatch[1]
    }

    // Handle format: /oauth/providers/{provider}/authorize
    const providersFormatMatch = url.match(/\/oauth\/providers\/([^\/\?]+)\/authorize/)
    if (providersFormatMatch) {
      return providersFormatMatch[1]
    }

    // Handle old format: /oauth/{provider}/authorize
    const oldFormatMatch = url.match(/\/oauth\/([^\/]+)\/authorize/)
    return oldFormatMatch ? oldFormatMatch[1] : null
  },

  /**
   * Get provider display name
   */
  getProviderName(provider?: string): string {
    if (!provider) return 'External Service'
    const p = toCardProvider(provider)

    const names: Record<string, string> = {
      google: 'Google',
      microsoft: 'Microsoft',
      github: 'GitHub',
      monday: 'Monday',
      discord: 'Discord',
      slack: 'Slack',
      linkedin: 'LinkedIn',
      facebook: 'Facebook',
      instagram: 'Instagram',
      twitter: 'X',
      pinterest: 'Pinterest',
    }

    return names[p] || p.charAt(0).toUpperCase() + p.slice(1)
  },

  /**
   * Get provider icon emoji
   */
  getProviderIcon(provider?: string): string {
    if (!provider) return '🔐'
    const p = toCardProvider(provider)

    const icons: Record<string, string> = {
      google: '🔵',
      microsoft: '🟦',
      github: '⚫',
      monday: '🟣',
      discord: '🟣',
      slack: '🟢',
      linkedin: '🔷',
      facebook: '🔵',
      instagram: '📸',
      twitter: '𝕏',
      pinterest: '📌',
    }

    return icons[p] || '🔐'
  },
  /**
   * Get provider display name (more comprehensive than getProviderName)
   */
  getProviderDisplayName(provider: string): string {
    return getOAuthProviderDisplayName(provider)
  },

  /**
   * Get provider logo asset
   */
  getProviderLogo(provider: string): string {
    // Normalize internal ids (e.g. linkedin_org → linkedin) before the lookup;
    // the shared metadata only knows user-facing providers.
    const logoUrl = getOAuthProviderLogoUrl(toCardProvider(provider))

    return logoUrl || takaLogo
  },

  /**
   * Check if provider is coming soon (not yet implemented)
   */
  isProviderComingSoon(provider: string): boolean {
    return isOAuthProviderComingSoon(provider)
  },
}
